import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../auth/AuthContext';
import type { ClientAccount } from '../api/accountAssignmentApi';

// null = no client selected (use default/Emtrain context)
export type ClientContextSelection = ClientAccount | null;

export interface ClientContextValue {
  clientContext: ClientContextSelection;
  setClientContext: (clientContext: ClientContextSelection) => void;
  registerClientContextChangeGuard?: (
    guard: () => boolean | Promise<boolean>,
  ) => () => void;
}

const ClientContext = createContext<ClientContextValue | undefined>(undefined);

interface ClientContextProviderProps {
  children: ReactNode;
}

// User-scoped persistence so a restored selection never bleeds across users.
// The selection is still validated per-page by the context picker, which resets
// it to Standard when the stored account is not assigned to the opened content.
const storageKey = (userId: string | undefined): string | null =>
  userId ? `authorai.clientContext.${userId}` : null;

function readStoredClientContext(userId: string | undefined): ClientContextSelection {
  const key = storageKey(userId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ClientAccount;
    return parsed && typeof parsed.id === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredClientContext(userId: string | undefined, value: ClientContextSelection): void {
  const key = storageKey(userId);
  if (!key) return;
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Ignore storage failures (private mode / quota); context stays in-memory.
  }
}

export function ClientContextProvider({ children }: ClientContextProviderProps) {
  const { user } = useAuth();
  const [clientContext, setClientContextState] = useState<ClientContextSelection>(
    () => readStoredClientContext(user?.id)
  );
  const contextChangeGuardRef = useRef<(() => boolean | Promise<boolean>) | null>(null);
  const contextTransitionIdRef = useRef(0);

  // On logout / user change, reload the selection from that user's storage
  // (null for a fresh user). Keyed on user id so token refreshes don't wipe it.
  useEffect(() => {
    setClientContextState(readStoredClientContext(user?.id));
  }, [user?.id]);

  const setClientContext = useCallback(
    (next: ClientContextSelection) => {
      const commit = () => {
        writeStoredClientContext(user?.id, next);
        setClientContextState(next);
      };
      const guard = contextChangeGuardRef.current;
      if (!guard) {
        commit();
        return;
      }
      const transitionId = ++contextTransitionIdRef.current;
      void Promise.resolve()
        .then(() => guard())
        .then((allowed) => {
          if (allowed && transitionId === contextTransitionIdRef.current) commit();
        })
        .catch(() => undefined);
    },
    [user?.id]
  );

  const registerClientContextChangeGuard = useCallback((
    guard: () => boolean | Promise<boolean>,
  ) => {
    contextChangeGuardRef.current = guard;
    return () => {
      if (contextChangeGuardRef.current === guard) contextChangeGuardRef.current = null;
    };
  }, []);

  return (
    <ClientContext.Provider value={{
      clientContext,
      setClientContext,
      registerClientContextChangeGuard,
    }}>
      {children}
    </ClientContext.Provider>
  );
}

/** Converts a ClientAccount id string to a numeric externalAccountId for the API, or null for base. */
export function toScopeAccountId(id: string | undefined): number | null {
  if (!id) return null;
  const n = Number(id);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function useClientContext(): ClientContextValue {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClientContext must be used within a ClientContextProvider');
  }
  return context;
}

/**
 * Ask Expert visibility override for learner previews. Base/Emtrain context
 * previews keep Ask Expert visible (product decision); client contexts defer to
 * the account config via the shared AccountConfigProvider. Returns `true` to
 * force it on in base context, or `undefined` to let the account config decide.
 */
export function useAskExpertPreviewOverride(): true | undefined {
  const { clientContext } = useClientContext();
  return toScopeAccountId(clientContext?.id) === null ? true : undefined;
}
